
import { Button } from '@mui/material';
import Page from '../Page';
import { connect } from 'react-redux';
import Portal from './Portal';
import swal from 'sweetalert';
import { hideLoading, showLoading } from '../../loading';
import actions from '../../redux/actions';
import { USER_ROLE } from '../../../../shared/constants';
import { MetaTags } from 'react-meta-tags';
import MetaTitle from '../../components/MetaTitle';
import request from '../../request';
import { delay, logout } from '../../utils';
import Loans from './Loans';
import Users from './Users';
import Agents from './Agents';
import Products from './Products';
import Customers from './Customers';
import UsersIcon from '@mui/icons-material/People';
import ProductsIcon from '@mui/icons-material/Inventory';
import LoansIcon from '@mui/icons-material/CreditScore';
import AgentsIcon from '@mui/icons-material/SupportAgent';
import CustomersIcon from '@mui/icons-material/Groups';
import { hasIntersection, isGuest } from '../../../../shared/utils';
import Installations from './Installations';
import InstallationIcon from '@mui/icons-material/Construction';
import DeliveryIcon from '@mui/icons-material/LocalShipping';
import Deliveries from './Deliveries';
import AgentEarningsIcon from '@mui/icons-material/LocalAtm';
import AgentEarnings from './AgentEarnings';
import LoanPaymentIcon from '@mui/icons-material/RequestQuote';
import LoanPayments from './LoanPayments';
import CashRequisitionIcon from '@mui/icons-material/Money';
import CashRequisitions from './CashRequisitions';
import TokensIcon from '@mui/icons-material/Numbers';
import Tokens from './Tokens';
import ReportIcon from '@mui/icons-material/Assessment';
import Reports from './Reports';


// helpers
function removeTrailingSlash(path) {
   return path.replace(/\/$/, '');
}

// constants
const ROUTES = [
   {
      path: '/reports',
      caption: 'Reports',
      icon: ReportIcon,
      element: <Reports />
   },
   {
      path: '/users',
      caption: 'Users',
      icon: UsersIcon,
      element: <Users />,
      shouldDisplay(roles) {
         return hasIntersection(roles, [ USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN ]);
      }
   },
   {
      path: '/customers',
      caption: 'Customers',
      icon: CustomersIcon,
      element: <Customers />,
      shouldDisplay(roles) {
         const allowedRoles =  [ USER_ROLE.SALES_MANAGER, USER_ROLE.CREDIT_MANAGER, USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN, USER_ROLE.TECHNICAL_MANAGER, USER_ROLE.FINANCE_MANAGER ];
         return hasIntersection(roles, allowedRoles);
      }
   },
   {
      path: '/loans',
      caption: 'Loans',
      icon: LoansIcon,
      element: <Loans />
   },
   {
      path: '/agents',
      caption: 'Agents',
      icon: AgentsIcon,
      element: <Agents />,
      shouldDisplay(roles) {
         const allowedRoles = [ USER_ROLE.SALES_MANAGER, USER_ROLE.TECHNICAL_MANAGER, USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN ];
         return hasIntersection(roles, allowedRoles);
      }
   },
   {
      path: '/products',
      caption: 'Products',
      icon: ProductsIcon,
      element: <Products />,
      shouldDisplay(roles) {
         const allowedRoles =  [ USER_ROLE.SALES_MANAGER, USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN, USER_ROLE.GUEST ];
         return hasIntersection(roles, allowedRoles);
      }
   },
   {
      path: '/installations',
      caption: 'Installations',
      icon: InstallationIcon,
      element: <Installations />,
      shouldDisplay(roles) {
         const allowedRoles =  [ USER_ROLE.TECHNICAL_MANAGER, USER_ROLE.CREDIT_MANAGER, USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN ];
         return hasIntersection(roles, allowedRoles);
      }
   },
   {
      path: '/deliveries',
      caption: 'Deliveries',
      icon: DeliveryIcon,
      element: <Deliveries />,
      shouldDisplay(roles) {
         const allowedRoles =  [ USER_ROLE.TECHNICAL_MANAGER, USER_ROLE.CREDIT_MANAGER, USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN ];
         return hasIntersection(roles, allowedRoles);
      }
   },
   {
      path: '/agent-earnings',
      caption: 'Agent Earnings',
      icon: AgentEarningsIcon,
      element: <AgentEarnings />,
      shouldDisplay(roles) {
         return !isGuest(roles);
      }
   },
   {
      path: '/loan-payments',
      caption: 'Loan Payments',
      icon: LoanPaymentIcon,
      element: <LoanPayments />,
      shouldDisplay(roles) {
         const allowedRoles =  [ USER_ROLE.CREDIT_MANAGER, USER_ROLE.FINANCE_MANAGER, USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN ];
         return hasIntersection(roles, allowedRoles);
      }
   },
   {
      path: '/cash-requisitions',
      caption: 'Cash Requisitions',
      icon: CashRequisitionIcon,
      element: <CashRequisitions />,
      shouldDisplay(roles) {
         const allowedRoles =  Object.values(USER_ROLE).filter(role => role !== USER_ROLE.GUEST);
         return hasIntersection(roles, allowedRoles);
      }
   },
   {
      path: '/tokens',
      caption: 'Tokens',
      icon: TokensIcon,
      element: <Tokens />,
      shouldDisplay(roles) {
         return hasIntersection(roles, [ USER_ROLE.SUPER_ADMIN, USER_ROLE.ADMIN, USER_ROLE.FINANCE_MANAGER, USER_ROLE.CREDIT_MANAGER, USER_ROLE.TECHNICAL_MANAGER ]);
      }
   }
]


class DashboardUnconnected extends Page {

   getProfile = async () => {

      try {

         showLoading();
         const res = await request.get('/api/auth/profile');
         actions.setUser(res.data);
         
      } catch (err) {
         const status = err.status;

         if (status >= 400 && status < 500)
            logout();

         swal('Error', err.message, 'error');

      } finally {
         hideLoading();
      }
   }

   createPortalProps() {
      const roles = this.props.user.roles;

      const allowedRoutes = ROUTES.filter(route => {
         if (typeof route.shouldDisplay === 'function')
            return route.shouldDisplay(roles);
         return true;
      });

      const routes = allowedRoutes.map(route => {
         return {
            ...route,
            path: `${removeTrailingSlash(route.path)}/*`
         }
      });

      const sidebarItems = allowedRoutes.map(route => {
         const { caption, icon, path } = route;
         return {  caption, icon, path };
      });

      const defaultPath = allowedRoutes[0].path;

      return {
         title: 'Dashboard',
         sidebarItems,
         defaultPath,
         routes,
      }

   }

   async componentDidMount() {
      super.componentDidMount();

      if (!this.props.authenticated) {
         await delay(100);
         const currentRoute = this.props.currentRoute;
         window.App.redirect('/login?redirect=' + currentRoute);
      }

      this.getProfile();
      
   }

   _render() {

      let pageContent;

      if (!this.props.user) {
         pageContent = <div className='page-height flex justify-center items-center'>
            <div>
               <p className='text-lg text-gray-500'>
                  Failed to load user profile
               </p>
               <Button fullWidth onClick={this.getProfile}>
                  Retry
               </Button>
            </div>
         </div>
      } else {
         const props = this.createPortalProps();
         pageContent = <Portal {...props} />
      }

      return <>
			<MetaTags>
            <MetaTitle>Dashboard</MetaTitle>
         </MetaTags>
         {pageContent}
      </>
  }
}

const mapStateToProps = state => {
   return {
      user: state.user,
      authenticated: state.authenticated,
      currentRoute: state.currentRoute
   }
}

const Dashboard = connect(mapStateToProps)(DashboardUnconnected);
export default Dashboard;